<template>
  <div :id="id" :class="['card-tabs', styleClass, cardGridClasses]" :style="style">
    <div ref="header" :class="[{ headerStyleClass }]" class="card__header">
      <div :class="{ 'card__header__tabs--dropdown': dropdownMode }" class="card__header__tabs">
        <div v-for="(item, index) in tabTitles" :class="[{ 'card__header__tabs__tab--active': index === activeCardIndex }, { 'hidden': item.display === false }]" class="card__header__tabs__tab" @click="changeCard(index)"><h1>{{ item.title }}<span v-if="!isNil(item.indicator)" :class="`card_header__tabs__tab__indicator card_header__tabs__tab__indicator--${item.indicator}`">•</span></h1></div>
        <div v-click-outside="hideDropdown" class="card__header__tabs__tab card__header__tabs__tab__dropdown" @click="dropdownVisible = !dropdownVisible">
          <h1>{{ $t('layout.tabableCard.more') }}</h1><icon icon="down"></icon>
          <div :class="{'card__header__tabs__tab__dropdown__wrapper--active' : dropdownVisible}" class="card__header__tabs__tab__dropdown__wrapper">
            <div v-for="(item, index) in tabTitles" :class="[{ 'hidden card__header__tabs__tab__dropdown__item--active': index === activeCardIndex }, { 'hidden': item.display === false }]" class="card__header__tabs__tab__dropdown__item" @click="changeCard(index)"><h1>{{ item.title }}<span v-if="!isNil(item.indicator)" :class="`card_header__tabs__tab__indicator card_header__tabs__tab__indicator--${item.indicator}`">•</span></h1></div>
          </div>
        </div>
        <resize-sensor @resize="onresize"></resize-sensor>
      </div>
      <div :class="{'card__header__right--has-button': hasButton}" class="card__header__right">
        <div class="card__header__right__content">
          <slot name="header-right" />
        </div>
        <div v-if="!isNil($slots['header-button'])" class="card__header__right__button">
        </div>
      </div>
    </div>
    <div :class="[{ contentStyleClass }, {'card__content--max-height': !isNil(maxHeight) }]"
         :style="cardContentStyle"
         :data-parentid="parentId"
         class="card__content"
         style="padding:0!important;">
      <slot />
    </div>
  </div>
</template>

<script>

import GridMixin from '@/mixins/GridMixin';
import { isNil, isEmpty, map, filter, sampleSize, findIndex } from 'lodash';
import ResizeSensor from 'vue-resize-sensor';
import Bowser from 'bowser';
import UpdateHotelSettingsMixin from '../../../mixins/UpdateHotelSettingsMixin';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'TabableCard',

  components: {
    'resize-sensor': ResizeSensor,
  },

  mixins: [GridMixin, TimerMixin],

  props: {
    title: String,
    msg: 'Swag',
    styleClass: String,
    headerStyleClass: String,
    contentStyleClass: String,
    parentId: String,
    cardContentStyle: String,
    cols: {
      default: 4,
    },
    maxHeight: {
      type: String,
      required: false,
      default: null,
    },
    minHeight: {
      type: String,
      required: false,
      default: null,
    },
    activeCard: {
      type: Number,
      required: false,
      default: 0,
    },
    watchActiveCard: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      activeCardIndex: this.activeCard,
      dropdownMode: false,
      dropdownVisible: false,
      maxHeaderWidth: 0,
      bowser: Bowser,
      refreshTabsTimer: null,
      tabTitles: [],
    };
  },

  computed: {
    id: function () {
      const id = this.$attrs.id || `comp_${this._uid}`;
      return isEmpty(this._parentHtmlId) ? id : `${this._parentHtmlId}-${id}`;
    },
    _parentHtmlId: function () {
      let it = this.$parent;
      while (!isNil(it)) {
        if (!isEmpty(it.id)) {
          return it.id;
        }
        it = it.$parent;
      }
      return '';
    },
    isSafari: function () {
      return Bowser.getParser(window.navigator.userAgent).satisfies({
        safari: '>=6',
      }) === true;
    },
    style: function () {
      if (isEmpty(this.maxHeight) && isEmpty(this.minHeight)) return {};

      if (isEmpty(this.maxHeight) && !isEmpty(this.minHeight)) return { minHeight: this.minHeight };

      return {
        minHeight: this.minHeight,
        maxHeight: this.maxHeight,
        overflow: 'hidden',
      };
    },
    tabs: function () {
      return isNil(this.$slots.default) ? [] : map(filter(this.$slots.default, it => !isNil(it.componentInstance)), it => it.componentInstance);
    },
    hasButton: function () {
      return !isNil(this.$slots['header-button']);
    },
    cardGridClasses: function () {
      return this.gridClasses;
    },
  },

  watch: {
    activeCardIndex: function (newVal) {
      this.$emit('activeCardIndex', newVal);
    },
    activeCard: function (newVal) {
      if (this.watchActiveCard) {
        this.changeCard(newVal);
      }
    },
  },

  created: function () {
    this.dropdownMode = (this.isSafari && this.tabTitles.length > 1);
    this.tabTitles = isNil(this.$slots.default) ? [] : map(filter(this.$slots.default, it => !isNil(it.componentOptions)), it => ({ title: it.componentOptions.propsData.title, display: it.componentOptions.propsData.display !== false, indicator: it.componentOptions.propsData.indicator }));
    this.setInterval(() => {
      this.tabTitles = isNil(this.$slots.default) ? [] : map(filter(this.$slots.default, it => !isNil(it.componentOptions)), it => ({ title: it.componentOptions.propsData.title, display: it.componentOptions.propsData.display !== false, indicator: it.componentOptions.propsData.indicator }));
    }, 1000);
  },

  beforeDestroy: function () {
  },

  mounted: function () {
    // make sure we pre-select the first visible card
    this.activeCardIndex = findIndex(this.tabTitles, 'display');
    this.changeCard(this.activeCardIndex);
  },

  methods: {
    isNil: function (test) {
      return isNil(test);
    },
    changeCard: function (index) {
      this.activeCardIndex = index;
      this.tabs.forEach((val, idx) => {
        val.visible = (idx === index);
      });
    },
    onresize: function (resize) {
      if (!isNil(resize) && !isNil(this.$refs.header)) {
        const headerWidth = this.$refs.header.clientWidth;
        this.maxHeaderWidth = Math.max(this.maxHeaderWidth, this.$refs.header.scrollWidth);

        this.dropdownMode = (this.isSafari && this.tabTitles.length > 1) || this.maxHeaderWidth > headerWidth;
      }
    },
    hideDropdown: function () {
      this.dropdownVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~styles/import";

  .card__content {
    border-left: .1rem solid #bfbfbf;
    border-right: .1rem solid #bfbfbf;
    border-bottom: .1rem solid #bfbfbf;
  }

  .card__header__tabs__tab {
    h1 {
      display: flex;
      flex-flow: row;
    }
  }

  .card_header__tabs__tab__indicator {
    font-size: 200%;
    line-height: 50%;
    padding-left: 0.4rem;

    &--green {
      color: $mint;
    }
    &--orange {
      color: $orange;
    }
  }

</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
  @import '~styles/components/lib/Card';
</style>



// WEBPACK FOOTER //
// src/components/lib/layout/TabableCard.vue